import React, { useMemo } from "react";
import Iterator from "../utils/Iterator";
import Visibility from "../utils/Visibility";
import { useTranslation } from "react-i18next";
import SubOptionModel from "../../Models/SubOptionModel";
import OptionModel from "../../Models/OptionModel";
import UAEDirhamIcon from "../utils/UAEDirhamIcon";

function OptionsProduct({
  options,
  name,
  price,
  setOptionsSelected,
  optionsSelected,
}: {
  options: OptionModel[];
  name: string;
  price: number;
  setOptionsSelected: any;
  optionsSelected: any;
}) {
  const { t } = useTranslation();

  // حساب المجموع الكلي للخيارات المختارة
  const totalOptionsPrice = useMemo(() => {
    if (!optionsSelected || optionsSelected.length === 0) return 0;
    
    let total = 0;
    optionsSelected.forEach((opt: any) => {
      if (opt.subOption && opt.subOption.price) {
        total += opt.subOption.price;
      }
    });
    return total;
  }, [optionsSelected]);

  return (
    <div className="flex flex-col gap-4 text-foreground">
      <div className="flex flex-col gap-4">
        <Iterator
          data={options}
          render={(e) => (
            <OptionsList
              option={e}
              key={e.id}
              name={name as keyof OptionModel}
              optionsSelected={optionsSelected}
              setOptionsSelected={setOptionsSelected}
            />
          )}
        />
      </div>
      <div className="border-b-2 border-border"></div>
      <p className="flex gap-1 justify-end text-[15px] font-semibold">
        <span className="font-bold">{t("gloabl.totalPrice")}:</span>{" "}
        <span className="flex items-center gap-1 text-primary">
          <UAEDirhamIcon className="w-4 h-4" />
          {totalOptionsPrice + price}
        </span>
      </p>
    </div>
  );
}
const OptionsList = ({
  option,
  name,
  optionsSelected,
  setOptionsSelected,
}: {
  option: OptionModel;
  name: keyof OptionModel;
  optionsSelected: any[];
  setOptionsSelected: any;
}) => {
  // التحقق من الخيارات المختارة لهذا الـ option
  const isSelected = (subOptionId: number) => {
    return optionsSelected.some(
      (opt: any) => opt.subOption && opt.subOption.id === subOptionId
    );
  };

  const handleToggle = (subOption: SubOptionModel) => {
    const isCurrentlySelected = isSelected(subOption.id!);
    
    setOptionsSelected((prev: any[]) => {
      const clone = [...prev];
      
      if (isCurrentlySelected) {
        // إزالة الخيار إذا كان مختاراً
        return clone.filter(
          (opt: any) => !(opt.subOption && opt.subOption.id === subOption.id)
        );
      } else {
        // إضافة الخيار إذا لم يكن مختاراً
        clone.push({ subOption: subOption, option: option });
        return clone;
      }
    });
  };

  const optionName = option[name] as string;
  
  return (
    <div className="flex flex-col gap-3">
      <p className="text-base md:text-lg font-bold text-foreground">
        {optionName || ""}
      </p>
      <Visibility visible={option.sub_options && option.sub_options.length > 0}>
        <div className="flex flex-wrap gap-2">
          <Iterator
            data={option.sub_options}
            render={(e: SubOptionModel) => (
              <SubOptionButton
                key={e.id}
                subOption={e}
                name={name}
                isSelected={isSelected(e.id!)}
                onClick={() => handleToggle(e)}
              />
            )}
          />
        </div>
      </Visibility>
    </div>
  );
};

const SubOptionButton = ({
  subOption,
  name,
  isSelected,
  onClick,
}: {
  subOption: SubOptionModel;
  name: string;
  isSelected: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 md:px-5 md:py-3 rounded-xl font-semibold text-sm md:text-base transition-all duration-300 flex items-center gap-2 border-2 ${
        isSelected
          ? "bg-primary text-onPrimary border-primary shadow-md hover:bg-primary/90"
          : "bg-background text-foreground border-border hover:border-primary/50 hover:bg-muted/50"
      }`}
    >
      <span>{subOption[name as keyof SubOptionModel]}</span>
      <Visibility visible={subOption.price > 0}>
        <span
          className={`flex items-center gap-1 ${
            isSelected ? "text-onPrimary" : "text-primary"
          }`}
          dir="ltr"
        >
          <UAEDirhamIcon className="w-3 h-3 md:w-4 md:h-4" />
          {subOption.price}
        </span>
      </Visibility>
    </button>
  );
};
export default OptionsProduct;
